// How billing screens show dates. Two kinds, never mixed:
//  - a calendar day (a date of service, a payer's status date) is the same day
//    for every reader, whatever their browser's zone;
//  - a moment (received, posted, created) is shown in Eastern time, where staff
//    work — not in the browser's zone, and not in UTC.

export const DISPLAY_TIME_ZONE = 'America/New_York'

type DateInput = string | Date | null | undefined

const MM_DD_YYYY: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' }

const BARE_DAY = /^(\d{4})-?(\d{2})-?(\d{2})$/

// A bare `YYYY-MM-DD` / `YYYYMMDD` names a day, not a moment: `new Date()`
// would read it as UTC midnight, which is the day before in the Americas.
const formatBareDay = (value: string | Date): string | null => {
  const bare = typeof value === 'string' ? BARE_DAY.exec(value.trim()) : null
  return bare ? `${bare[2]}/${bare[3]}/${bare[1]}` : null
}

const toValidDate = (value: string | Date): Date | null => {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * A calendar day as MM/DD/YYYY. A timestamp is read by its UTC day: calendar
 * days are stored as UTC midnight (ERA), Eastern midnight (typed in) or UTC
 * noon (generated), and all three sit on the right UTC day — read in the
 * browser's zone, the first one is the day before.
 */
export const formatCalendarDate = (value: DateInput, empty = '-'): string => {
  if (!value) return empty
  const date = toValidDate(value)
  return formatBareDay(value) ?? (date ? date.toLocaleDateString('en-US', { ...MM_DD_YYYY, timeZone: 'UTC' }) : empty)
}

/** The Eastern day a moment fell on, as MM/DD/YYYY. */
export const formatEasternDate = (value: DateInput, empty = '-'): string => {
  if (!value) return empty
  const date = toValidDate(value)
  return formatBareDay(value) ?? (date ? date.toLocaleDateString('en-US', { ...MM_DD_YYYY, timeZone: DISPLAY_TIME_ZONE }) : empty)
}

/** The Eastern day a moment fell on, as YYYY-MM-DD — what a date input holds. */
export const easternDay = (value: DateInput): string => {
  if (!value) return ''
  const bare = typeof value === 'string' ? BARE_DAY.exec(value.trim()) : null
  if (bare) return `${bare[1]}-${bare[2]}-${bare[3]}`
  const date = toValidDate(value)
  // en-CA formats as YYYY-MM-DD
  return date ? date.toLocaleDateString('en-CA', { timeZone: DISPLAY_TIME_ZONE }) : ''
}

/** A moment in Eastern time, as MM/DD/YYYY hh:mm AM. */
export const formatEasternDateTime = (value: DateInput, empty = '-'): string => {
  const date = value ? toValidDate(value) : null
  if (!date) return empty
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: DISPLAY_TIME_ZONE })
  return `${formatEasternDate(date)} ${time}`
}

/** Today's Eastern day, stored the way a date of service is (UTC noon of that day). */
export const todayAsServiceDate = (): string => {
  // en-CA formats as YYYY-MM-DD
  const day = new Date().toLocaleDateString('en-CA', { timeZone: DISPLAY_TIME_ZONE })
  return `${day}T12:00:00.000Z`
}

const US_DAY = /^(\d{2})\/(\d{2})\/(\d{4})$/

/**
 * A calendar day as YYYY-MM-DD, read the way formatCalendarDate reads it, so
 * days can be compared; '' when there is none. Remittance lines spell a date
 * of service MM/DD/YYYY.
 */
export const calendarDayKey = (value: DateInput): string => {
  if (!value) return ''
  if (typeof value === 'string') {
    const bare = BARE_DAY.exec(value.trim())
    if (bare) return `${bare[1]}-${bare[2]}-${bare[3]}`
    const us = US_DAY.exec(value.trim())
    if (us) return `${us[3]}-${us[1]}-${us[2]}`
  }
  const date = toValidDate(value)
  return date ? date.toISOString().slice(0, 10) : ''
}

/**
 * Service lines in date-of-service order, earliest first. Lines on the same
 * day keep the order they came in, and a line with no date goes last.
 */
export const sortByDateOfService = <T>(items: T[], dateOf: (item: T) => DateInput): T[] =>
  items
    .map((item, index) => ({ item, index, day: calendarDayKey(dateOf(item)) }))
    .sort((a, b) => {
      if (a.day === b.day) return a.index - b.index
      if (!a.day || !b.day) return a.day ? -1 : 1
      return a.day < b.day ? -1 : 1
    })
    .map(({ item }) => item)
